/**
 * #3173 — keep the magic-link secret out of `window.location.href`.
 *
 * The magic link is `/auth/verify?token=<JWT>&email=<addr>[&redirect=…]`
 * (backend/src/services/email.js). Anything that reads the page URL before
 * VerifyPage has consumed it gets the raw token and email: PostHog's pageview
 * `$current_url`, the `$session_entry_url` it attaches to every event in the
 * session, the `$initial_current_url` it sends in the `/flags` request's
 * `person_properties` (no sanitize hook runs on that request), the rrweb Meta
 * event of the first session-replay snapshot, and Sentry's pageload span and
 * error replay. Redacting each of those individually is whack-a-mole; this
 * removes the secret at the root instead.
 *
 * The primary scrub is the inline <script id="tacit-magic-link-scrub"> at the
 * top of frontend/index.html's <head>: it has to run before the LinkedIn and
 * Meta pixels, which are synchronous inline scripts that beacon the URL and
 * run before any module. `scrubMagicLinkFromLocation()` below is a second
 * pass (a no-op when the inline script already ran): it runs from
 * src/scrubMagicLinkUrl.js, which main.jsx imports FIRST — before Sentry
 * (instrument.js) and before `posthog.init()`. It moves `token`/`email` into module memory and
 * `history.replaceState`s to a URL without them. Every other param
 * (`redirect`, `ref`) stays in the URL. VerifyPage reads the secret back
 * through `getMagicLinkParam()`.
 *
 * Module memory, not sessionStorage: the token is single-use, so a reload
 * after the scrub could not have succeeded anyway, and a storage copy would
 * outlive its use.
 *
 * #3258 extends the same scrub to the digest-unsubscribe link,
 * `/unsubscribe/digest/<token>` (backend/src/services/email.js), where the
 * bearer token is a PATH segment: the scrub stashes it and replaceState's to
 * the bare `/unsubscribe/digest`; UnsubscribePage reads it back through
 * `getDigestUnsubscribeToken()`. Unlike the magic link this token is not
 * single-use, but it is long-lived, so module memory is still preferred over
 * storage: a storage copy would be readable by the ad pixels on every later
 * page of the tab. The cost is that a reload of the scrubbed page shows the
 * "missing token" error.
 *
 * #3262 item 1 — the LinkedIn and Meta loaders in index.html do not load
 * their vendor scripts on /auth/verify or /unsubscribe/…: those pages hold a
 * live secret in a window global the vendor code could read. On /auth/verify
 * each loader queues itself on `window.__tacitDeferredTags` instead, and
 * `markMagicLinkConsumed()` releases the queue (`releaseDeferredThirdPartyTags`)
 * once the token has been spent. The lintrk/fbq stubs stay defined, so a
 * conversion call made before the release is queued, not lost. On
 * /unsubscribe/… the vendor scripts are never loaded for that page load.
 *
 * #3262 item 1 P1 review-fix (PR #3271) — a prior version also released on
 * every exit from VerifyPage's FAILURE view (Google fallback sign-in,
 * "request a new link", "go home"), to recover ad-conversion tracking for a
 * sign-in later in the same document. That is unsafe: a failed verify does
 * not prove the stashed token is dead — the one-time-use check
 * (AuthService.verifyMagicLinkToken) is an atomic Redis GETDEL, so a
 * client-side network error or the backend's Redis-outage path leaves the
 * bearer token fully redeemable while still sitting in
 * window.__tacitMagicLinkStash (frozen, but readable by any script). See the
 * comment on VerifyPage's handleRequestNewLink. Releasing is intentionally
 * tied to markMagicLinkConsumed() alone, the one path the GETDEL is
 * guaranteed to have already consumed the token.
 */

const SECRET_PARAMS = ['token', 'email'];
const DIGEST_UNSUBSCRIBE_BARE = '/unsubscribe/digest';

let stashed = null;
let consumed = false;
let digestToken = null;

function decodePathSegment(segment) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * #3262 review-fix — classify a pathname the way React Router matches routes:
 * case-insensitive, each `/`-separated segment percent-decoded on its own (so
 * an encoded `%2F` never becomes a separator), trailing slashes ignored. A
 * case-sensitive raw-text regex missed `/UNSUBSCRIBE/DIGEST/<t>` and
 * `/%75nsubscribe/digest/<t>`, both of which still render UnsubscribePage.
 * Mirrors the inline head script in frontend/index.html, which publishes the
 * same `kind` as `window.__tacitSecretPath` for the ad-pixel loaders.
 * @returns {{kind: ''|'verify'|'unsubscribe', digestToken: string|null}}
 */
export function classifySecretPath(pathname) {
  const segs = String(pathname ?? '').split('/');
  while (segs.length > 1 && segs[segs.length - 1] === '') segs.pop();
  const norm = segs.map((seg) => decodePathSegment(seg).toLowerCase());
  let kind = '';
  if (norm[0] === '' && norm[1] === 'unsubscribe') kind = 'unsubscribe';
  else if (segs.length === 3 && norm[0] === '' && norm[1] === 'auth' && norm[2] === 'verify') kind = 'verify';
  const digestToken = kind === 'unsubscribe' && segs.length === 4 && norm[2] === 'digest' && segs[3] !== ''
    // Decoded the way react-router's useParams decodes the :token segment; case kept.
    ? decodePathSegment(segs[3])
    : null;
  return { kind, digestToken };
}

/**
 * If the current location is a magic link carrying `token` or `email`,
 * stash them and rewrite the URL without them. No-op on every other URL.
 * @returns {boolean} true when the URL was rewritten
 */
export function scrubMagicLinkFromLocation(win = globalThis.window) {
  const location = win?.location;
  const history = win?.history;
  if (!location || typeof history?.replaceState !== 'function') return false;
  if (classifySecretPath(location.pathname).kind !== 'verify') return false;

  const params = new URLSearchParams(location.search);
  const found = {};
  for (const name of SECRET_PARAMS) {
    if (params.has(name)) {
      found[name] = params.get(name);
      params.delete(name);
    }
  }
  if (Object.keys(found).length === 0) return false;

  const query = params.toString();
  try {
    history.replaceState(
      history.state,
      '',
      `${location.pathname}${query ? `?${query}` : ''}${location.hash}`,
    );
  } catch {
    // URL left as-is, so VerifyPage still reads it from searchParams.
    return false;
  }
  stashed = found;
  return true;
}

/**
 * A magic-link param for VerifyPage. Order: the live URL (an in-app
 * navigation to a full link is never scrubbed), then this module's stash,
 * then the stash index.html's inline head script wrote to
 * `window.__tacitMagicLinkStash` (that script runs first in production, so on
 * a real load the module scrub finds a clean URL and this is where the values
 * are). Same decoding as `URLSearchParams.get`, so callers are unchanged.
 * Both stashes are ignored once VerifyPage has consumed a successful link, so
 * a later in-app visit to a bare /auth/verify cannot replay it.
 */
export function getMagicLinkParam(searchParams, name) {
  const fromUrl = searchParams?.get(name);
  if (fromUrl !== null && fromUrl !== undefined) return fromUrl;
  if (consumed) return null;
  return stashed?.[name] ?? globalThis.window?.__tacitMagicLinkStash?.[name] ?? null;
}

/**
 * VerifyPage calls this after a successful verification. The inline stash is
 * non-configurable by design (nothing can overwrite it), so it is ignored
 * rather than cleared.
 */
export function markMagicLinkConsumed(win = globalThis.window) {
  consumed = true;
  stashed = null;
  releaseDeferredThirdPartyTags(win);
}

/**
 * #3262 item 1 — run the ad-pixel loaders index.html deferred on /auth/verify.
 * Called only once the magic-link token is spent (markMagicLinkConsumed), so
 * vendor code never runs while an unspent token sits in
 * window.__tacitMagicLinkStash. Idempotent: the queue is emptied first.
 * @returns {number} how many loaders ran
 */
export function releaseDeferredThirdPartyTags(win = globalThis.window) {
  const queue = win?.__tacitDeferredTags;
  if (!Array.isArray(queue) || queue.length === 0) return 0;
  const loaders = queue.splice(0, queue.length);
  for (const load of loaders) {
    try {
      if (typeof load === 'function') load();
    } catch {
      // A vendor loader failing must never break sign-in.
    }
  }
  return loaders.length;
}

/**
 * #3258 — if the current location is `/unsubscribe/digest/<token>`, stash the
 * token and rewrite the URL to `/unsubscribe/digest` (query and hash kept).
 * No-op on every other URL, and when the inline head script already ran.
 * @returns {boolean} true when the URL was rewritten
 */
export function scrubDigestUnsubscribeFromLocation(win = globalThis.window) {
  const location = win?.location;
  const history = win?.history;
  if (!location || typeof history?.replaceState !== 'function') return false;
  const { digestToken: token } = classifySecretPath(location.pathname);
  if (token === null) return false;

  try {
    history.replaceState(
      history.state,
      '',
      `${DIGEST_UNSUBSCRIBE_BARE}${location.search}${location.hash}`,
    );
  } catch {
    // URL left as-is, so UnsubscribePage still reads it from the route param.
    return false;
  }
  digestToken = token;
  return true;
}

/**
 * The digest-unsubscribe token for UnsubscribePage. Order: the route param
 * (an in-app navigation to the full link is never scrubbed), then this
 * module's stash, then the stash index.html's inline head script wrote to
 * `window.__tacitDigestUnsubscribeStash`.
 */
export function getDigestUnsubscribeToken(routeToken) {
  if (typeof routeToken === 'string' && routeToken !== '') return routeToken;
  return digestToken ?? globalThis.window?.__tacitDigestUnsubscribeStash?.token ?? null;
}

export function __resetMagicLinkStashForTests() {
  stashed = null;
  consumed = false;
  digestToken = null;
}
